<template>
  <el-card shadow="never" v-loading="loading">
    <template slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: 'Audit' }">Audit</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: 'Audit commercial' }">
          Commercial
        </el-breadcrumb-item>
      </el-breadcrumb>
    </template>

    <div>
      Region:
      <el-select v-model="selectedRegion" placeholder="Select a region" @change="changeRegion">
        <el-option v-for="region in regions" :key="region" :label="region" :value="region" />
      </el-select>
    </div>

    <el-divider></el-divider>

    <div class="commercial-wrapper">
      <el-card shadow="never" v-loading="noPhotos.loading">
        <template slot="header">
          <div class="photos-header">
            <span>Photos</span>
            <el-switch
              v-model="noPhotos.vacant"
              active-color="#13ce66"
              inactive-color="#ff4949"
              :disabled="selectedRegion === ''"
              active-text="Vacant"
              inactive-text="All"
              @change="(status) => changeVacancy(status)"
            >
            </el-switch>
          </div>
        </template>

        <el-card shadow="never" v-if="noPhotos.stats.length" style="margin-bottom: 1rem">
          <div class="stats-wrapper">
            <div v-for="stat in noPhotos.stats" :key="stat._id">
              {{ stat.facilityCode }}: {{ stat.count }}
            </div>
          </div>
        </el-card>

        <el-card shadow="never" :body-style="{ padding: 0 }">
          <el-table :height="500" :data="noPhotos.units" @row-click="routeToUnit" @sort-change="(obj) => fetchWithSort('noPhotos', obj)">
            <el-table-column :width="150" prop="name" label="Name"></el-table-column>
            <el-table-column :width="150" prop="facility.code" label="Facility" :sortable="noPhotos.units.length > 1">
              <template slot-scope="scope">
                {{ facilityMap[scope.row.facility._id].code }}
              </template>
            </el-table-column>
            <el-table-column prop="status" label="Status" :sortable="noPhotos.units.length > 1">
              <template slot="header">
                <el-select
                  label="Lease Status"
                  v-model="noPhotos.status"
                  :disabled="selectedRegion === ''"
                  size="small"
                  placeholder="Status"
                  no-match-text="Status not set"
                  no-data-text="Status not set"
                  @change="(status) => changeStatus('noPhotos', status)"
                >
                  <el-option
                    v-for="status in statuses"
                    :key="status"
                    :label="$options.filters.printableStatus(status)"
                    :value="status"
                  >
                  </el-option>
                </el-select>
              </template>
              <template slot-scope="scope">
                <el-tag effect="plain"  class="status-text" :color="statusColor(scope.row.status)">
                  {{ scope.row.status | printableStatus }}
                </el-tag>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            background
            layout="prev, pager, next"
            :total="noPhotos.totalCount"
            :hide-on-single-page="true"
            :current-page.sync="noPhotos.pageNumber"
            @current-change="getNoPhotoUnits"
          >
          </el-pagination>
        </el-card>
      </el-card>

      <el-card shadow="never">
        <template slot="header">
          Unavailable
        </template>

        <el-card shadow="never" :body-style="{ padding: 0 }">
          <el-table :height="580" :data="unavailable.units" @row-click="routeToUnit" v-loading="unavailable.loading" @sort-change="(obj) => fetchWithSort('unavailable', obj)">
            <el-table-column prop="name" label="Name"></el-table-column>
            <el-table-column prop="computedSize" label="Size" :sortable="unavailable.units.length > 1"></el-table-column>
            <el-table-column prop="facility.code" label="Facility" :sortable="unavailable.units.length > 1">
              <template slot-scope="scope">
                {{ facilityMap[scope.row.facility._id].code }}
              </template>
            </el-table-column>
            <el-table-column prop="unavailableSince" label="Since" :sortable="unavailable.units.length > 1">
              <template slot-scope="scope">
                <span v-if="scope.row.unavailableSince">{{ scope.row.unavailableSince | formatDate }}</span>
                <span v-else>N/A</span>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            background
            layout="prev, pager, next"
            :total="unavailable.totalCount"
            :hide-on-single-page="true"
            :current-page.sync="unavailable.pageNumber"
            @current-change="getUnavailableUnits"
          >
          </el-pagination>
        </el-card>
      </el-card>

      <el-card shadow="never">
        <template slot="header">
          Overdue Move-In
        </template>

        <el-card shadow="never" :body-style="{ padding: 0 }">
            <el-table
              v-loading="moveIn.loading"
              @sort-change="(obj) => fetchWithSort('moveIn', obj)"
              :data="moveIn.rentals"
              :header-cell-style="{padding: '0.25rem'}"
              :default-sort = "{prop: 'moveIn', order: 'ascending'}"
              style="width: 100%; padding: 0"
              :height="580"
              @row-click="routeToUnit"
              >
              <el-table-column
                prop="unit.name"
                :width="100"
                label="Unit"
              >
              </el-table-column>
              <el-table-column
                prop="unit.computedSize"
                :width="100"
                label="Size"
              >
              </el-table-column>
              <el-table-column
                prop="facility.code"
                :width="120"
                sortable
                label="Location"
              >
              </el-table-column>
              <el-table-column
                prop="customer.company"
                :width="120"
                label="Company"
                :formatter="(row, column, cellValue) => cellValue && cellValue.length > 15 ? `${cellValue.slice(0, 12)}...` : cellValue"
              >
              </el-table-column>
              <el-table-column
                label="Move In"
                prop="moveIn"
                :width="120"
                sortable
                :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue)"
                >
                <!-- <template slot-scope="scope">
                  <span v-if="scope.row.moveIn">
                    {{ new Date( scope.row.moveIn).toLocaleDateString() }}
                  </span>
                </template> -->
              </el-table-column>
              <el-table-column
                prop="unit.status"
                label="Status"
                :width="120"
                :formatter="(row, column, cellValue) => $options.filters.printableStatus(cellValue)"
                >
              </el-table-column>
              <el-table-column
                label="Required Items"
                :width="165"
              >
                <template slot-scope="scope">
                  <div v-if="scope.row.unitInvoice && scope.row.unitInvoice.pendingItems && scope.row.unitInvoice.pendingItems.length > 0">
                    <el-tooltip
                      v-for="item in scope.row.unitInvoice.pendingItems"
                      :key="item._id"
                      :content="`${item.reason} ${item.value ? $options.filters.currencySign(item.value, $root.state.facilityData) : ''}`"
                    >
                      <el-tag size="small">{{ item.key === "Payment" ? '$' : item.key }}</el-tag>
                    </el-tooltip>
                  </div>
                  <el-tag type="success" size="small" v-else>Ready</el-tag>
                </template>
              </el-table-column>
          </el-table>
          <el-pagination
            background
            layout="prev, pager, next"
            :total="moveIn.totalCount"
            :hide-on-single-page="true"
            :current-page.sync="moveIn.pageNumber"
            @current-change="getMoveInUnits"
          >
          </el-pagination>
        </el-card>
      </el-card>

      <el-card shadow="never">
        <template slot="header">
          Overdue Move-Out
        </template>

        <el-card shadow="never" :body-style="{ padding: 0 }">
            <el-table
              :data="moveOut.units"
              v-loading="moveOut.loading"
              :header-cell-style="{padding: '0.25rem'}"
              :default-sort = "{prop: 'invoice.end', order: 'ascending'}"
              style="width: 100%; padding: 0"
              :height="580"
              @sort-change="(obj) => fetchWithSort('moveOut', obj)"
              @row-click="routeToUnit"
            >
              <el-table-column
                prop="name"
                :width="100"
                label="Unit"
              >
              </el-table-column>
              <el-table-column
                prop="facility.code"
                :width="100"
                :sortable="moveOut.units.length > 1"
                label="Facility"
              >
              </el-table-column>
              <el-table-column
                prop="customer.company"
                label="Company"
                :width="120"
                :formatter="(row, column, cellValue) => cellValue && cellValue.length > 15 ? `${cellValue.slice(0, 12)}...` : cellValue"
              >
              </el-table-column>
                  <el-table-column
                    prop="computedSize"
                    label="Size"
                  >
                  </el-table-column>
                  <el-table-column
                    label="Move Out"
                    prop="invoice.end"
                    :sortable="moveOut.units.length > 1"
                    :width="120"
                    :formatter="(row, column, cellValue) => $options.filters.formatDate(cellValue)"
                  >
                    <!-- <template slot-scope="scope">
                      <span v-if="scope.row.invoice && scope.row.invoice.end">
                        {{ new Date( scope.row.invoice.end).toLocaleDateString() }}
                      </span>
                    </template> -->
                  </el-table-column>
                  <el-table-column
                    prop="status"
                    label="Status"
                    :width="120"
                    :formatter="(row, column, cellValue) => $options.filters.printableStatus(cellValue)"
                    >
                  </el-table-column>
                </el-table>
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="moveOut.totalCount"
                  :hide-on-single-page="true"
                  :current-page.sync="moveOut.pageNumber"
                  @current-change="getMoveOutUnits"
                >
                </el-pagination>
        </el-card>
      </el-card>
    </div>
  </el-card>
</template>

<script>
import { ADMIN_API_BASE, AUDIT_API, ORG_API } from '../env';
import statuses from '../utils/status';
import axios from 'axios';

export default {
  name: 'AuditCommercial',
  props: {
    facility: { type: String, required: false },
  },
  watch: {
    photoUnitsVacant: function() {
      this.getUnits();
    }
  },
  mounted() {
    this.getOrg();
    this.getFacilities();
  },
  data() {
    return {
      loading: true,
      statuses,
      facilities: [],
      facilityMap: {},
      facilityCodes: [],
      orgTheme: [],
      noPhotos: {
        sortQuery: {},
        vacant: false,
        pageNumber: 1,
        loading: false,
        totalCount: 0,
        status: '',
        stats: [],
        units: [],
      },
      unavailable: {
        sortQuery: {},
        vacant: false,
        pageNumber: 1,
        loading: false,
        totalCount: 0,
        status: '',
        stats: [],
        units: [],
      },
      moveIn: {
        sortQuery: {},
        vacant: false,
        pageNumber: 1,
        loading: false,
        totalCount: 0,
        status: '',
        stats: [],
        rentals: [],
      },
      moveOut: {
        sortQuery: {},
        vacant: false,
        pageNumber: 1,
        loading: false,
        totalCount: 0,
        status: '',
        stats: [],
        units: [],
      },
      selectedRegion: '',
    };
  },
  computed: {
    regions() {
      if (!this.facilities.length) { return []; }
      return Array.from(new Set(this.facilities.map(facility => facility.region)));
    },
    facilityUnitCount() {
      const count = {};
      if (!this.facilityCodes.length) { return {}; }
      for (const code of this.facilityCodes) {
        count[code] = 0;
      }

      if (!this.noPhotoUnits.length) { return count; }
      for (const unit of this.noPhotoUnits) {
        const facility = this.facilityMap[unit.facility];
        count[facility.code] += 1;
      }

      return count;
    },
  },
  methods: {
    changeVacancy(status) {
      if (this.noPhotos.vacant) { this.noPhotos.status = 'ALL'; }
      this.getNoPhotoUnits();
    },
    changeStatus(unitType, status) {
      this.getNoPhotoUnits();
    },
    changeRegion() {
      this.noPhotos.pageNumber = 1;
      this.getUnits();
    },
    fetchWithSort(type, { column, prop, order }) {
      const orderNumberMap = {
        ascending: 1,
        descending: -1,
        null: 0,
      };

      const typeDataMap = {
        noPhotos: this.noPhotos,
        unavailable: this.unavailable,
        moveIn: this.moveIn,
        moveOut: this.moveOut,
      };
      const typeFuncMap = {
        noPhotos: this.getNoPhotoUnits,
        unavailable: this.getUnavailableUnits,
        moveIn: this.getMoveInUnits,
        moveOut: this.getMoveOutUnits,
      };

      let sortQuery = {};
      if (order !== null) {
        sortQuery = {
          sortBy: prop,
          sortOrder: orderNumberMap[order],
        }
      }

      typeDataMap[type].sortQuery = sortQuery;
      typeFuncMap[type]();
    },
    async getNoPhotoUnits() {
      this.noPhotos.loading = true;


      const response = await axios
        .get(`${AUDIT_API}/commercial`, {
          params: {
            ...this.noPhotos.sortQuery,
            ...(this.noPhotos.status && this.noPhotos.status !== 'ALL' ? { status: this.noPhotos.status } : {}),
            type: 'noPhotos',
            vacant: this.noPhotos.vacant,
            region: this.selectedRegion,
            pageNumber: this.noPhotos.pageNumber,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

      const { units, stats, totalCount, pageNumber } = response.data;
      this.noPhotos = {
        ...this.noPhotos,
        units,
        stats: stats.map(stat => ({ ...stat, facilityCode: this.facilityMap[stat._id].code })),
        totalCount,
        pageNumber,
      }

      this.noPhotos.loading = false;
    },
    async getUnavailableUnits() {
      this.unavailable.loading = true;

      const response = await axios
        .get(`${AUDIT_API}/commercial`, {
          params: {
            ...this.unavailable.sortQuery,
            ...(this.unavailable.status ? { status: this.unavailable.status } : {}),
            type: 'unavailable',
            region: this.selectedRegion,
            pageNumber: this.unavailable.pageNumber,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

      const { units, totalCount, pageNumber } = response.data;
      this.unavailable = {
        ...this.unavailable,
        units,
        totalCount,
        pageNumber,
      }

      this.unavailable.loading = false;
    },
    async getMoveInUnits() {
      this.moveIn.loading = true;

      const response = await axios
        .get(`${AUDIT_API}/commercial`, {
          params: {
            ...this.moveIn.sortQuery,
            ...(this.moveIn.status ? { status: this.moveIn.status } : {}),
            type: 'moveIn',
            region: this.selectedRegion,
            pageNumber: this.moveIn.pageNumber,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

      const { rentals, totalCount, pageNumber } = response.data;
      this.moveIn = {
        ...this.moveIn,
        rentals,
        totalCount,
        pageNumber,
      }

      this.moveIn.loading = false;
    },
    async getMoveOutUnits() {
      this.moveOut.loading = true;

      const response = await axios
        .get(`${AUDIT_API}/commercial`, {
          params: {
            ...this.moveOut.sortQuery,
            ...(this.moveOut.status ? { status: this.moveOut.status } : {}),
            type: 'moveOut',
            region: this.selectedRegion,
            pageNumber: this.moveOut.pageNumber,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

      const { units, totalCount, pageNumber } = response.data;
      this.moveOut = {
        ...this.moveOut,
        units,
        totalCount,
        pageNumber,
      }

      this.moveOut.loading = false;
    },
    async getUnits() {
      this.getNoPhotoUnits();
      this.getUnavailableUnits();
      this.getMoveInUnits();
      this.getMoveOutUnits();
    },
    async getOrg() {
      try {
        const org = await axios.get(`${ORG_API}/theme`, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.orgTheme = org.data;
      } catch (err) {
        this.$message.warning({
          title: 'Warning',
          message: 'Org theme not accessible',
        });
      }
    },
    async getFacilities() {
      this.loading = true;
      const response = await axios
        .get(`${ADMIN_API_BASE}/facilities`, {
          params: {
            limit: 55,
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

      this.facilities = response.data.filter(facility => facility.region);
      for (const facility of this.facilities) {
        this.facilityMap[facility._id] = facility;
      }
      this.loading = false;
    },
    statusColor(status) {
      const [foundColor] = this.orgTheme.filter((item) => {
        if (status === item.type) {
          return item;
        }
      });
      return foundColor.color || '#efefef';
    },
    routeToUnit(row) {
      // eslint-disable-next-line no-underscore-dangle
      if (row.unit) { return this.routeToUnit(row.unit); }

      const facilityId = row.facility._id ? row.facility._id : row.facility;
      this.$router.push({ name: 'Facility Unit', params: { unit: row._id, facility: facilityId } });
    },
  }
}
</script>

<style scoped>
.status-text{
  color: beige;
  border: none;
  text-align: center;
}

.commercial-wrapper {
  display: flex;
  flex-direction: column;
}

.photos-header {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 1rem;
}

.stats-wrapper {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-evenly;
  gap: 1rem
}

@media (min-width: 768px) {
  .commercial-wrapper {
    display: grid;
    gap: 1rem;
    grid-template-rows: auto auto;
    grid-template-columns: 1fr 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AuditCommercial.vue